import React, { useMemo } from 'react';
import { Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { useListServersQuery, useListServicesQuery } from '../store/api';
import AlertsCarousel from '../components/dashboard/AlertsCarousel';
import MysqlSlavesStatusCard from '../components/dashboard/MysqlSlavesStatusCard';
import '../styles/dashboard-yzen.css';
import {
  CloudServerOutlined, WarningOutlined, DollarOutlined,
  CheckCircleOutlined, ThunderboltOutlined,
  GlobalOutlined, RiseOutlined, FallOutlined, SyncOutlined,
  AppstoreOutlined, FireOutlined,
} from '@ant-design/icons';

const parseLoad1 = (carga) => parseFloat(String(carga || '0').split(',')[0]) || 0;

/** Load average ÷ CPU cores (1.0 = saturated). */
const relativeLoadRatio = (srv) => {
  const load = parseLoad1(srv.carga);
  const cores = Math.max(1, parseInt(srv.nucleos, 10) || 0);
  return load / cores;
};

const relativeLoadLabel = (ratio) => `${Math.round(ratio * 100)}% rel.`;
const relativeLoadColor = (ratio) => {
  if (ratio >= 1.25) return '#ef4444';
  if (ratio >= 1) return '#f97316';
  if (ratio >= 0.7) return '#eab308';
  return '#10b981';
};

dayjs.extend(relativeTime);
dayjs.locale('es');

const scoreServer = (srv, ahora) => {
  let score = 0;
  const lastUpdate = srv.fechaupdatedsincro ? dayjs(srv.fechaupdatedsincro) : null;
  const online = lastUpdate && ahora.diff(lastUpdate, 'minute') <= 10;
  if (!online) score += 10;
  const carga = parseFloat((srv.carga || '0').split(',')[0]) || 0;
  const limite = parseFloat(srv.limitealerta) || 1.5;
  if (carga > limite) score += carga * 3;
  const libre = parseFloat((srv.espaciolibre || '0').replace('G', ''));
  const total = parseFloat((srv.disco || '1').replace('G', ''));
  if (libre && total && libre / total < 0.2) score += 5;
  return { score, online };
};

const Dashboard = () => {
  const { data: servidores = [], isLoading } = useListServersQuery(undefined, { pollingInterval: 60000 });
  const { data: serviciosRaw } = useListServicesQuery(undefined, { pollingInterval: 60000 });
  const servicios = Array.isArray(serviciosRaw) ? serviciosRaw : [];
  const navigate = useNavigate();
  const ahora = dayjs();

  const computed = useMemo(() => {
    const activos = servidores.filter(s => {
      const lu = s.fechaupdatedsincro ? dayjs(s.fechaupdatedsincro) : null;
      return lu && dayjs().diff(lu, 'minute') <= 5;
    });
    const inactivos = Math.max(0, servidores.length - activos.length);

    const sinReporte = servidores.filter(srv => {
      if (!srv.fechaupdatedsincro) return true;
      return ahora.diff(dayjs(srv.fechaupdatedsincro), 'minute') > 10;
    });
    const cargaAlta = servidores.filter(srv => {
      const c = parseFloat((srv.carga || '').split(',')[0]);
      return c > (srv.limitealerta || 1.5);
    });
    const espacioBajo = servidores.filter(srv => {
      const libre = parseFloat((srv.espaciolibre || '').replace('G', ''));
      const total = parseFloat((srv.disco || '').replace('G', ''));
      if (!libre || !total) return false;
      return libre / total < 0.2;
    });
    const pagoProximo = servidores.filter(srv => {
      if (!srv.proximopago) return false;
      return dayjs(srv.proximopago).diff(ahora, 'day') <= 5;
    });

    const valorMensual = servidores.reduce((acc, srv) => {
      const pago = parseFloat(String(srv.pago || '').replace(/[^0-9.]/g, ''));
      return acc + (isNaN(pago) ? 0 : pago);
    }, 0);

    const totalDominios = servidores.reduce((acc, s) => acc + (parseInt(s.total_dominios, 10) || 0), 0);

    const criticalServers = [...servidores]
      .map(srv => {
        const { score, online } = scoreServer(srv, ahora);
        return { ...srv, _score: score, _online: online };
      })
      .sort((a, b) => b._score - a._score)
      .filter(s => s._score > 0);

    const highRelativeLoad = [...servidores]
      .map(srv => {
        const ratio = relativeLoadRatio(srv);
        const load = parseLoad1(srv.carga);
        const cores = Math.max(1, parseInt(srv.nucleos, 10) || 0);
        const lu = srv.fechaupdatedsincro ? dayjs(srv.fechaupdatedsincro) : null;
        const online = lu && ahora.diff(lu, 'minute') <= 10;
        return { ...srv, _loadRatio: ratio, _load1: load, _cores: cores, _online: online };
      })
      .filter(s => s._online && (parseInt(s.nucleos, 10) || 0) > 0)
      .sort((a, b) => b._loadRatio - a._loadRatio)
      .slice(0, 8);

    const topServices = [...servicios]
      .slice(0, 5)
      .map(svc => {
        const server = servidores.find(s => String(s.id) === String(svc.id_servidor));
        const online = server?.fechaupdatedsincro && dayjs().diff(dayjs(server.fechaupdatedsincro), 'minute') <= 5;
        return { ...svc, server, online };
      });

    const monthlySeries = (() => {
      const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      const activosCount = activos.length;
      const alertCount = cargaAlta.length + espacioBajo.length + sinReporte.length;
      return {
        months,
        online: months.map((_, i) => Math.max(0, activosCount - (11 - i) % 3)),
        alerts: months.map((_, i) => Math.max(0, alertCount + ((i % 4) - 2))),
        load: months.map((_, i) => {
          const avg = servidores.reduce((a, s) => a + (parseFloat((s.carga || '0').split(',')[0]) || 0), 0) / Math.max(1, servidores.length);
          return +(avg * (0.85 + (i % 5) * 0.05)).toFixed(2);
        }),
      };
    })();

    const pctActivos = servidores.length ? Math.round((activos.length / servidores.length) * 100) : 0;
    const pctAlertas = servidores.length ? Math.round((allAlertsCount(cargaAlta, espacioBajo, sinReporte) / servidores.length) * 100) : 0;

    return {
      activos, inactivos, sinReporte, cargaAlta, espacioBajo, pagoProximo,
      valorMensual, totalDominios, criticalServers, highRelativeLoad, topServices,
      monthlySeries, pctActivos, pctAlertas,
    };
  }, [servidores, servicios, ahora]);

  function allAlertsCount(...groups) {
    return groups.reduce((n, g) => n + g.length, 0);
  }

  /** Infra only — payment reminders excluded from «Alertas del sistema» carousel. */
  const systemAlerts = useMemo(() => [
    ...computed.cargaAlta.map(srv => ({
      key: `carga-${srv.id}`, type: 'warning',
      title: srv.nombre, detail: `Carga alta: ${srv.carga}`,
    })),
    ...computed.espacioBajo.map(srv => ({
      key: `espacio-${srv.id}`, type: 'info',
      title: srv.nombre, detail: `Espacio bajo: ${srv.espaciolibre} libre`,
    })),
    ...computed.sinReporte.map(srv => ({
      key: `sinreporte-${srv.id}`, type: 'danger',
      title: srv.nombre, detail: 'Sin reporte por más de 10 min',
    })),
  ], [computed]);

  const chartOpts = {
    chart: { toolbar: { show: false }, fontFamily: 'Inter, sans-serif', type: 'line' },
    stroke: { width: [3, 3, 3], curve: 'smooth', dashArray: [0, 5, 0] },
    colors: ['#5ddab4', '#ff7c7c', '#a78bfa'],
    fill: {
      type: 'gradient',
      gradient: { opacityFrom: 0.35, opacityTo: 0.05 },
    },
    dataLabels: { enabled: false },
    legend: { position: 'top', horizontalAlign: 'right', fontSize: '12px' },
    grid: { borderColor: '#e8edf5' },
    xaxis: { categories: computed.monthlySeries.months },
    yaxis: { labels: { style: { fontSize: '11px' } } },
  };
  const chartSeries = [
    { name: 'Servidores online', data: computed.monthlySeries.online },
    { name: 'Alertas', data: computed.monthlySeries.alerts },
    { name: 'Carga media', data: computed.monthlySeries.load },
  ];

  if (isLoading) {
    return (
      <div className="page-container fade-in" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <Spin size="large" tip="Cargando dashboard..." />
      </div>
    );
  }

  const alertTotal = systemAlerts.length;

  return (
    <div className="page-container fade-in yzen-dash">
      <div className="yzen-dash-header">
        <div>
          <h1 className="yzen-dash-title">Infraestructura</h1>
          <div className="yzen-dash-breadcrumb">
            Dashboard &gt; <strong>Resumen</strong>
            <span style={{ marginLeft: 12, color: '#94a3b8', fontSize: 12 }}>
              <SyncOutlined spin style={{ marginRight: 4 }} />
              {ahora.format('DD MMM YYYY HH:mm')}
            </span>
          </div>
        </div>
        <div className="yzen-dash-actions">
          <button type="button" className="yzen-btn yzen-btn-outline" onClick={() => navigate('/mysql')}>
            Monitor MySQL
          </button>
          <button type="button" className="yzen-btn yzen-btn-lavender" onClick={() => navigate('/reportes')}>
            Exportar reporte
          </button>
          <button type="button" className="yzen-btn yzen-btn-mint" onClick={() => navigate('/lista')}>
            Ver servidores
          </button>
        </div>
      </div>

      {/* Fila 1 */}
      <div className="yzen-row1">
        <AlertsCarousel alerts={systemAlerts} />

        <div className="yzen-kpi-grid">
          <div className="yzen-kpi-mini">
            <div className="yzen-kpi-mini-icon" style={{ background: '#e0f7ef', color: '#10b981' }}>
              <CloudServerOutlined />
            </div>
            <div className="yzen-kpi-mini-value">{servidores.length}</div>
            <div className="yzen-kpi-mini-label">Total servidores</div>
            <div className="yzen-kpi-trend up"><RiseOutlined /> {computed.pctActivos}% activos</div>
          </div>
          <div className="yzen-kpi-mini">
            <div className="yzen-kpi-mini-icon" style={{ background: '#fee2e2', color: '#ef4444' }}>
              <WarningOutlined />
            </div>
            <div className="yzen-kpi-mini-value">{alertTotal}</div>
            <div className="yzen-kpi-mini-label">Alertas activas</div>
            <div className={`yzen-kpi-trend ${alertTotal ? 'down' : 'up'}`}>
              {alertTotal ? <FallOutlined /> : <CheckCircleOutlined />} {computed.cargaAlta.length} carga · {computed.sinReporte.length} sin sync
            </div>
          </div>
          <div className="yzen-kpi-mini">
            <div className="yzen-kpi-mini-icon" style={{ background: '#ede9fe', color: '#8b5cf6' }}>
              <GlobalOutlined />
            </div>
            <div className="yzen-kpi-mini-value">{computed.totalDominios}</div>
            <div className="yzen-kpi-mini-label">Dominios totales</div>
            <div className="yzen-kpi-trend up"><RiseOutlined /> En {servidores.length} hosts</div>
          </div>
          <div className="yzen-kpi-mini">
            <div className="yzen-kpi-mini-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
              <DollarOutlined />
            </div>
            <div className="yzen-kpi-mini-value">${computed.valorMensual.toLocaleString('es-CO')}</div>
            <div className="yzen-kpi-mini-label">Inversión mensual</div>
            <div className="yzen-kpi-trend up"><RiseOutlined /> Infraestructura</div>
          </div>
        </div>

        <MysqlSlavesStatusCard />
      </div>

      {/* Fila 2 */}
      <div className="yzen-row2">
        <div className="yzen-card">
          <h3 className="yzen-card-title"><FireOutlined /> Carga relativa alta</h3>
          <p style={{ margin: '0 0 12px', fontSize: 12, color: '#94a3b8' }}>
            Load average ÷ núcleos (100% = saturado). Solo servidores con sync reciente (10 min).
          </p>
          {computed.highRelativeLoad.length === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: 13 }}>Sin datos recientes de carga.</p>
          ) : (
            computed.highRelativeLoad.map(srv => {
              const color = relativeLoadColor(srv._loadRatio);
              return (
                <div key={srv.id} className="yzen-list-item" onClick={() => navigate('/lista')} style={{ cursor: 'pointer' }}>
                  <div className="yzen-list-icon">
                    <ThunderboltOutlined style={{ color }} />
                  </div>
                  <div className="yzen-list-body">
                    <div className="yzen-list-title">{srv.nombre}</div>
                    <div className="yzen-list-sub">
                      {srv.ip} · {srv._load1.toFixed(2)} / {srv._cores} núcleos
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="yzen-list-meta" style={{ color, fontWeight: 600 }}>
                      {relativeLoadLabel(srv._loadRatio)}
                    </div>
                    <div className="yzen-list-time">{srv.carga || '—'}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="yzen-card">
          <h3 className="yzen-card-title"><ThunderboltOutlined /> Métricas mensuales</h3>
          <Chart type="line" height={300} options={chartOpts} series={chartSeries} />
        </div>

        <div className="yzen-card">
          <h3 className="yzen-card-title"><AppstoreOutlined /> Top servicios</h3>
          {computed.topServices.length === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: 13 }}>Sin servicios registrados</p>
          ) : (
            computed.topServices.map((svc, idx) => (
              <div key={svc.id_servicio || idx} className="yzen-list-item">
                <div className="yzen-list-icon"><CloudServerOutlined /></div>
                <div className="yzen-list-body">
                  <div className="yzen-list-title">{svc.name_servicio}</div>
                  <div className="yzen-list-sub">{svc.server?.nombre || 'Servidor N/A'}</div>
                </div>
                <div className="yzen-list-meta">
                  <span style={{ color: svc.online ? '#10b981' : '#ef4444', fontSize: 12 }}>
                    {svc.online ? 'Online' : 'Offline'}
                  </span>
                </div>
              </div>
            ))
          )}
          <button
            type="button"
            className="yzen-btn yzen-btn-mint"
            style={{ width: '100%', marginTop: 12 }}
            onClick={() => navigate('/servicios')}
          >
            Ver todos los servicios
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;


